import React, { useCallback, useContext, useRef, useState } from 'react';
import { AxiosResponse } from 'axios';
import Form from '@rjsf/core';
import styled from "@emotion/styled";
import { Box } from '@mui/material';
import axios from '@/_utils/axios';
import { ApiActionMethods, ExceptionApiAction } from '@/redux/slices/slice_types';
import { ConfigContext } from '@/contextAPI/configContext';
import { Dialog } from '@/components/_common';
import { useToast } from '@/components/_common/Toast';
import SchemaForm from '@/components/_common/SchemaForm/SchemaForm';
import { useRefreshExceptions } from '@/components/Exceptions/hooks/refreshExceptions.hook';
import { getActionSuccessMessage } from '@/components/Exceptions/utils/utils';

interface ApiActionButtonChildrenProps {
  dialog: React.ReactNode;
  iconName: string;
  openDialog(): void;
}

interface Props {
  action: ExceptionApiAction;
  dialogConfirmationMessage: string;
  children(childrenProps: ApiActionButtonChildrenProps): React.ReactNode;
}

/**
 * This component:
 * * provides a confirmation dialog and the function that shows this dialog
 * * reloads exceptions when an action is successfully performed
 * * performs back-end calls based on `action.details`
 * * provides iconName for an action button
 */
const ApiActionButton: React.FC<Props> = ({ action, dialogConfirmationMessage, children }) => {
  const bulkActionIcons: Record<string, string> | undefined = useContext(ConfigContext).bulkActionIcons;
  const { success, error } = useToast();
  const refreshExceptions = useRefreshExceptions();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [schemaFormData, setSchemaFormData] = useState(action.detail.body);
  const schemaFormRef = useRef<Form | null>(null);
  const iconName = bulkActionIcons?.[action.name] || action.name;

  const openDialog = useCallback(() => {
    setIsDialogOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const handleSchemaFormChange = useCallback(({ formData }: any) => {
    setSchemaFormData(formData);
  }, []);

  const handleSubmit = useCallback(() => {
    const { method, url, body } = action.detail;

    if (schemaFormRef.current) {
      const isSchemFormValid = schemaFormRef.current.validateForm();
      if (!isSchemFormValid) return;
    }

    let performRequest: () => Promise<AxiosResponse>;

    // We clear baseURL parameter in requests because server provides the full url including `/api/v1`
    if (method === ApiActionMethods.Post) {
      performRequest = () => axios.post(url, schemaFormRef.current ? schemaFormRef.current.state.formData : body, { baseURL: '' });
    } else if (method === ApiActionMethods.Get) {
      if (schemaFormRef.current) {
        console.warn('Submitting schema form data is not supported at the moment for GET requests.');
      }
      performRequest = () => axios.get(url, { baseURL: '' });
    } else {
      throw new Error(`Unsupported method`);
    }
    setLoading(true);

    return performRequest()
      .then(() => {
        refreshExceptions();
        success(getActionSuccessMessage(action.name) ?? 'Action completed successfully.');
      })
      .catch(() => {
        error('Failed to perform action');
      })
      .finally(() => {
        setLoading(false);
        closeDialog();
      });
  }, [refreshExceptions, success, error, closeDialog]);

  const dialog = (
    <StyledDialog title="Confirm Action" open={isDialogOpen} submitText="Confirm" onCancel={closeDialog} onSubmit={handleSubmit} loading={loading}>
      <Box>{dialogConfirmationMessage}</Box>
      {action.detail.body_schema && (
        <Box mt={3}>
          <SchemaForm
            formRef={schemaFormRef}
            tagName="div"
            id="exception-api-action-schem-form"
            schema={action.detail.body_schema}
            formData={schemaFormData}
            onChange={handleSchemaFormChange}
          />
        </Box>
      )}
    </StyledDialog>
  );

  return children({ dialog, iconName, openDialog });
};

export default ApiActionButton;

const StyledDialog = styled(Dialog)(() => ({
  '& .MuiPaper-root': {
    maxWidth: 450
  }
}));
